import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Trophy, Medal, Crown } from 'lucide-react'
import { useState, useEffect } from 'react'

interface LeaderboardEntry {
  rank: number
  username: string
  score: number
  kills: number
  wins: number
  games: number
  country: string
}

export default function LeaderboardPage() {
  const navigate = useNavigate()
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Mock data - will be replaced with API call
    const mockData: LeaderboardEntry[] = [
      { rank: 1, username: 'NinjaPro', score: 15420, kills: 89, wins: 12, games: 45, country: 'US' },
      { rank: 2, username: 'StormBreaker', score: 12890, kills: 76, wins: 10, games: 38, country: 'UK' },
      { rank: 3, username: 'PhoenixRise', score: 11200, kills: 65, wins: 8, games: 42, country: 'CA' },
      { rank: 4, username: 'ShadowHunter', score: 9850, kills: 58, wins: 7, games: 35, country: 'DE' },
      { rank: 5, username: 'BlazeKing', score: 8900, kills: 52, wins: 6, games: 30, country: 'FR' },
      { rank: 6, username: 'IceQueen', score: 7650, kills: 45, wins: 5, games: 28, country: 'JP' },
      { rank: 7, username: 'ThunderBolt', score: 6200, kills: 38, wins: 4, games: 25, country: 'BR' },
      { rank: 8, username: 'DarkKnight', score: 5400, kills: 32, wins: 3, games: 22, country: 'AU' },
      { rank: 9, username: 'ViperStrike', score: 4300, kills: 26, wins: 2, games: 18, country: 'KR' },
      { rank: 10, username: 'WolfPack', score: 3200, kills: 19, wins: 1, games: 15, country: 'SE' },
    ]
    setEntries(mockData)
    setLoading(false)
  }, [])

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown size={20} className="text-yellow-400" />
    if (rank === 2) return <Medal size={20} className="text-gray-300" />
    if (rank === 3) return <Medal size={20} className="text-amber-600" />
    return <span className="text-white/40 w-5 text-center">{rank}</span>
  }

  return (
    <div className="relative w-full h-full flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-oded-dark via-oded-dark to-[#0d1a2d]" />
      
      <div className="relative z-10 flex flex-col h-full px-6 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/60 hover:text-oded-accent transition-colors"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="flex items-center gap-3">
            <Trophy size={28} className="text-oded-accent" />
            <h1 className="text-3xl font-bold neon-text">Leaderboard</h1>
          </div>
        </div>

        {/* Table */}
        <div className="panel flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-2 border-oded-accent/30 border-t-oded-accent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="overflow-auto h-full">
              <table className="w-full">
                <thead className="sticky top-0 bg-oded-panel">
                  <tr className="text-left text-white/50 text-sm">
                    <th className="px-4 py-3">Rank</th>
                    <th className="px-4 py-3">Player</th>
                    <th className="px-4 py-3 text-right">Score</th>
                    <th className="px-4 py-3 text-right">Kills</th>
                    <th className="px-4 py-3 text-right">Wins</th>
                    <th className="px-4 py-3 text-right">Games</th>
                    <th className="px-4 py-3 text-center">Country</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => (
                    <motion.tr
                      key={entry.rank}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`border-t border-white/5 hover:bg-white/5 transition-colors ${
                        entry.rank <= 3 ? 'bg-oded-accent/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center w-8">
                          {getRankIcon(entry.rank)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${
                          entry.rank === 1 ? 'text-yellow-400' :
                          entry.rank === 2 ? 'text-gray-300' :
                          entry.rank === 3 ? 'text-amber-600' :
                          'text-white'
                        }`}>
                          {entry.username}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-oded-accent">
                        {entry.score.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-oded-danger">
                        {entry.kills}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-oded-success">
                        {entry.wins}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-white/60">
                        {entry.games}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-lg">{entry.country}</span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}